import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Tabs, Empty, Spin } from 'antd';
import TrophyBadgeCard from '@/components/organisms/Card/TrohyBadgeCard';
import { adminStudentApi } from '@/services/api';

const { Title } = Typography;

interface StudentTrophyBadgeProps {
  studentId: string;
}

const StudentTrophyBadge: React.FC<StudentTrophyBadgeProps> = ({ studentId }) => {
  const [active, setActive] = useState('trophy');
  const [trophies, setTrophies] = useState<any[]>([]);
  const [badges, setBadges] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const academyStudentId = useMemo(() => {
    const n = Number(studentId);
    return Number.isFinite(n) ? n : undefined;
  }, [studentId]);

  const formatDate = (value?: string) => {
    if (!value) return '-';
    return String(value).slice(0, 10);
  };

  const aggregatedBadges = useMemo(() => {
    const map = new Map<string, any>();
    badges.forEach((badge: any) => {
      const key = String(badge?.trophyBadgeId ?? badge?.id ?? Math.random());
      const existing = map.get(key);
      if (existing) {
        existing.count = (existing.count || 1) + 1;
        if (!existing.awardedAt || (badge.awardedAt && String(badge.awardedAt) > String(existing.awardedAt))) {
          existing.awardedAt = badge.awardedAt;
        }
      } else {
        map.set(key, { ...badge, count: 1 });
      }
    });
    return Array.from(map.values());
  }, [badges]);

  const renderGrid = (items: any[], type: 'trophy' | 'badge') => {
    if (loading) {
      return (
        <div className='py-10 text-center'>
          <Spin />
        </div>
      );
    }
    if (!items.length) {
      return (
        <div className='py-10'>
          <Empty description="수여된 내역이 없습니다." />
        </div>
      );
    }
    return (
      <section className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4'>
        {items.map((item: any, index: number) => (
          <TrophyBadgeCard
            key={`${type}-${item.id ?? index}`}
            type={type}
            iconUrl={item.iconUrl}
            name={item.name}
            date={formatDate(item.awardedAt)}
            count={item.count || 1}
          />
        ))}
      </section>
    );
  };

  useEffect(() => {
    const fetch = async () => {
      if (!academyStudentId) return;
      setLoading(true);
      try {
        const [tRes, bRes]: any = await Promise.all([
          adminStudentApi.trophies(academyStudentId),
          adminStudentApi.badges(academyStudentId),
        ]);
        setTrophies(((tRes?.data || tRes) as any[]) || []);
        setBadges(((bRes?.data || bRes) as any[]) || []);
      } catch (e) {
        // silent
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [academyStudentId]);
  return (
    <div>
      <Title level={3} style={{ marginBottom: '16px' }}>
        트로피 및 뱃지
      </Title>
      <Tabs
        activeKey={active}
        onChange={setActive}
        items={[
          {
            key: 'trophy',
            label: loading ? '트로피 (로딩중)' : '트로피',
            children: renderGrid(trophies, 'trophy'),
          },
          {
            key: 'badge',
            label: loading ? '뱃지 (로딩중)' : '뱃지',
            children: renderGrid(aggregatedBadges, 'badge'),
          },
        ]}
      />
    </div>
  );
};

export default StudentTrophyBadge; 